import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle2, MoonStar } from "lucide-react";
import { useLang } from "../i18n";
import { SERVICES, TIME_SLOTS, serviceName } from "../siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function Booking({ selectedService, setSelectedService }) {
    const { lang, t } = useLang();
    const b = t.booking;
    const [step, setStep] = useState(0);
    const [date, setDate] = useState("");
    const [slot, setSlot] = useState("");
    const [booked, setBooked] = useState([]);
    const [closedDays, setClosedDays] = useState([]);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [notes, setNotes] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const days = useMemo(() => {
        const out = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date();
            d.setDate(d.getDate() + i);
            out.push(d);
        }
        return out;
    }, []);

    useEffect(() => {
        axios.get(`${API}/blocked-dates`).then((res) => setClosedDays(res.data.dates || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!date) return;
        setSlot("");
        axios
            .get(`${API}/bookings/slots`, { params: { date } })
            .then((res) => setBooked(res.data.booked || []))
            .catch(() => setBooked([]));
    }, [date]);

    const canNext = [!!selectedService, !!date, !!slot, name.trim().length > 0 && phone.trim().length >= 5][step];

    const next = () => {
        if (!canNext) {
            setError(b.errFill);
            return;
        }
        setError("");
        setStep((s) => Math.min(3, s + 1));
    };

    const submit = async () => {
        if (!canNext) {
            setError(b.errFill);
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/bookings`, {
                name: name.trim(),
                phone: phone.trim(),
                email: email.trim() || null,
                service: selectedService,
                date,
                time_slot: slot,
                notes: notes.trim(),
            });
            setResult(res.data);
        } catch (e) {
            setError(e.response?.status === 409 ? b.errSlotTaken : b.errSubmit);
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setResult(null);
        setStep(0);
        setDate("");
        setSlot("");
        setName("");
        setPhone("");
        setNotes("");
        setSelectedService("");
    };

    const stepVariants = {
        initial: { opacity: 0, x: 32 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -32 },
    };

    return (
        <section id="booking" className="relative mx-auto max-w-4xl px-4 py-28 sm:px-6" data-testid="booking-section">
            <motion.div
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className="text-center"
            >
                <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#FFB800]">{b.overline}</p>
                <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{b.title}</h2>
                <p className="mx-auto mt-3 max-w-xl text-[#8E8EA0]">{b.sub}</p>
            </motion.div>

            <div className="glow-amber mt-12 rounded-3xl border border-[#FFB800]/25 bg-[#13131A] p-6 sm:p-10">
                {result ? (
                    <motion.div
                        data-testid="booking-success-modal"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center"
                    >
                        <CheckCircle2 size={56} className="mx-auto text-[#FFB800]" />
                        <h3 className="mt-4 font-display text-2xl font-bold">{b.successTitle}</h3>
                        <p className="mt-2 text-[#8E8EA0]">{b.successSub}</p>
                        {result.email && (
                            <p className="mt-2 font-mono2 text-xs text-[#FFB800]">{b.successEmailNote}</p>
                        )}
                        <div className="mx-auto mt-7 max-w-md rounded-2xl border border-white/10 bg-black/40 p-6 text-left">
                            <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#8E8EA0]">{b.refLabel}</p>
                            <p className="font-mono2 text-2xl font-bold text-[#FFB800]">{result.reference}</p>
                            <div className="mt-4 space-y-1.5 border-t border-white/10 pt-4 font-mono2 text-sm text-[#c9c9d4]">
                                <p>{serviceName(result.service, lang)}</p>
                                <p>
                                    {result.date} · {result.time_slot}
                                </p>
                                <p>
                                    {result.name} · {result.phone}
                                </p>
                            </div>
                        </div>
                        <p className="mt-5 text-xs text-[#8E8EA0]">{b.addrNote}</p>
                        <button
                            onClick={reset}
                            className="mt-6 rounded-full border border-[#FFB800]/50 px-6 py-2.5 text-sm font-semibold text-[#FFB800] hover:bg-[#FFB800] hover:text-[#0B0B0E] transition-colors"
                        >
                            {b.another}
                        </button>
                    </motion.div>
                ) : (
                    <div data-testid="booking-form">
                        <div className="mb-9 flex items-center justify-between gap-2">
                            {b.steps.map((label, i) => (
                                <div key={label} className="flex flex-1 items-center gap-2">
                                    <span
                                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-mono2 text-xs transition-colors duration-300 ${
                                            i <= step
                                                ? "border-[#FFB800] bg-[#FFB800] font-bold text-[#0B0B0E]"
                                                : "border-white/15 text-[#8E8EA0]"
                                        }`}
                                    >
                                        {i + 1}
                                    </span>
                                    <span className={`hidden text-xs sm:block ${i <= step ? "text-[#F4F4F6]" : "text-[#8E8EA0]"}`}>
                                        {label}
                                    </span>
                                    {i < 3 && <span className="h-px flex-1 bg-white/10" />}
                                </div>
                            ))}
                        </div>

                        <AnimatePresence mode="wait">
                            {step === 0 && (
                                <motion.div key="s0" {...stepVariants} transition={{ duration: 0.35 }}>
                                    <p className="mb-4 font-mono2 text-xs uppercase tracking-[0.25em] text-[#8E8EA0]">{b.serviceLabel}</p>
                                    <div className="grid gap-3 sm:grid-cols-2" data-testid="booking-service-dropdown">
                                        {SERVICES.map((s) => (
                                            <button
                                                key={s.id}
                                                onClick={() => setSelectedService(s.id)}
                                                className={`rounded-2xl border p-5 text-left transition-all duration-300 ${
                                                    selectedService === s.id
                                                        ? "border-[#FFB800] bg-[#FFB800]/10 shadow-[0_0_24px_rgba(255,184,0,0.15)]"
                                                        : "border-white/10 hover:border-[#FFB800]/40"
                                                }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <p className="font-semibold text-[#F4F4F6]">{lang === "zh" ? s.name_zh : s.name_en}</p>
                                                    <p className="font-mono2 font-bold text-[#FFB800]">A${s.price}{lang === "zh" ? s.suffix_zh : s.suffix_en}</p>
                                                </div>
                                                <p className="mt-1 font-mono2 text-xs text-[#8E8EA0]">{lang === "zh" ? s.duration_zh : s.duration_en}</p>
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {step === 1 && (
                                <motion.div key="s1" {...stepVariants} transition={{ duration: 0.35 }}>
                                    <p className="mb-4 font-mono2 text-xs uppercase tracking-[0.25em] text-[#8E8EA0]">{b.dateLabel}</p>
                                    <div className="grid grid-cols-4 gap-3 sm:grid-cols-7" data-testid="booking-date-picker">
                                        {days.map((d) => {
                                            const val = fmtDate(d);
                                            const closed = closedDays.includes(val);
                                            return (
                                                <button
                                                    key={val}
                                                    disabled={closed}
                                                    onClick={() => setDate(val)}
                                                    className={`relative rounded-2xl border px-2 py-4 text-center transition-all duration-300 ${
                                                        closed
                                                            ? "cursor-not-allowed border-white/5 opacity-40"
                                                            : date === val
                                                              ? "border-[#FFB800] bg-[#FFB800]/10 shadow-[0_0_24px_rgba(255,184,0,0.15)]"
                                                              : "border-white/10 hover:border-[#FFB800]/40"
                                                    }`}
                                                >
                                                    <p className="text-xs text-[#8E8EA0]">{t.weekdays[d.getDay()]}</p>
                                                    <p className="mt-1 font-mono2 text-sm font-bold text-[#F4F4F6]">
                                                        {String(d.getMonth() + 1).padStart(2, "0")}/{String(d.getDate()).padStart(2, "0")}
                                                    </p>
                                                    {closed && (
                                                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-[#2a2a35] px-2 py-0.5 text-[9px] text-[#8E8EA0]">
                                                            {b.closedTag}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            )}

                            {step === 2 && (
                                <motion.div key="s2" {...stepVariants} transition={{ duration: 0.35 }}>
                                    <p className="mb-4 font-mono2 text-xs uppercase tracking-[0.25em] text-[#8E8EA0]">
                                        {b.slotLabel} · {date}
                                    </p>
                                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                                        {TIME_SLOTS.map((ts) => {
                                            const taken = booked.includes(ts);
                                            return (
                                                <button
                                                    key={ts}
                                                    data-testid="booking-timeslot-chip"
                                                    disabled={taken}
                                                    onClick={() => setSlot(ts)}
                                                    className={`relative rounded-2xl border py-4 font-mono2 text-sm font-bold transition-all duration-300 ${
                                                        taken
                                                            ? "cursor-not-allowed border-white/5 text-[#4a4a58] line-through"
                                                            : slot === ts
                                                              ? "border-[#FF2E4D] bg-[#FF2E4D]/10 text-[#FF2E4D] shadow-[0_0_24px_rgba(255,46,77,0.2)]"
                                                              : "border-white/10 text-[#F4F4F6] hover:border-[#FF2E4D]/50"
                                                    }`}
                                                >
                                                    <MoonStar size={12} className="mr-1.5 inline" />
                                                    {ts}
                                                    {taken && (
                                                        <span className="absolute -top-2 right-2 rounded-full bg-[#2a2a35] px-2 py-0.5 text-[9px] text-[#8E8EA0] no-underline">
                                                            {b.bookedTag}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            )}

                            {step === 3 && (
                                <motion.div key="s3" {...stepVariants} transition={{ duration: 0.35 }} className="space-y-5">
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <div>
                                            <label className="mb-2 block font-mono2 text-xs uppercase tracking-[0.25em] text-[#8E8EA0]">
                                                {b.nameLabel}
                                            </label>
                                            <input
                                                data-testid="booking-name-input"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder={b.namePh}
                                                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-[#F4F4F6] outline-none transition-colors focus:border-[#FFB800]"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-2 block font-mono2 text-xs uppercase tracking-[0.25em] text-[#8E8EA0]">
                                                {b.phoneLabel}
                                            </label>
                                            <input
                                                data-testid="booking-phone-input"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                placeholder={b.phonePh}
                                                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-[#F4F4F6] outline-none transition-colors focus:border-[#FFB800]"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-2 block font-mono2 text-xs uppercase tracking-[0.25em] text-[#8E8EA0]">
                                            {b.emailLabel}
                                        </label>
                                        <input
                                            data-testid="booking-email-input"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder={b.emailPh}
                                            className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-[#F4F4F6] outline-none transition-colors focus:border-[#FFB800]"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-2 block font-mono2 text-xs uppercase tracking-[0.25em] text-[#8E8EA0]">
                                            {b.notesLabel}
                                        </label>
                                        <textarea
                                            data-testid="booking-notes-input"
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder={b.notesPh}
                                            rows={3}
                                            className="w-full resize-none rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-[#F4F4F6] outline-none transition-colors focus:border-[#FFB800]"
                                        />
                                    </div>
                                    <div className="rounded-xl border border-[#FFB800]/20 bg-[#FFB800]/5 px-4 py-3 font-mono2 text-xs text-[#c9c9d4]">
                                        {serviceName(selectedService, lang)} · {date} · {slot}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {error && (
                            <p data-testid="booking-error" className="mt-5 text-center text-sm text-[#FF2E4D]">
                                {error}
                            </p>
                        )}

                        <div className="mt-9 flex items-center justify-between">
                            <button
                                onClick={() => {
                                    setError("");
                                    setStep((s) => Math.max(0, s - 1));
                                }}
                                disabled={step === 0}
                                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-2.5 text-sm text-[#8E8EA0] transition-colors hover:border-[#FFB800]/50 hover:text-[#FFB800] disabled:opacity-0"
                            >
                                <ArrowLeft size={15} />
                                {b.back}
                            </button>
                            {step < 3 ? (
                                <button
                                    onClick={next}
                                    className="inline-flex items-center gap-2 rounded-full bg-[#FFB800] px-7 py-2.5 text-sm font-semibold text-[#0B0B0E] transition-colors hover:bg-[#FF2E4D] hover:text-white"
                                >
                                    {b.next}
                                    <ArrowRight size={15} />
                                </button>
                            ) : (
                                <button
                                    data-testid="booking-submit-btn"
                                    onClick={submit}
                                    disabled={submitting}
                                    className="rounded-full bg-[#FF2E4D] px-8 py-2.5 text-sm font-semibold text-white transition-all hover:shadow-[0_0_32px_rgba(255,46,77,0.4)] disabled:opacity-60"
                                >
                                    {submitting ? b.submitting : b.submit}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
